/**
 * @file useBinLabelJobs.ts
 * @description Журнал печати этикеток ячеек склада (phase19.22 шаг 5):
 *   `GET admin/wms/labels/jobs?warehouseId=&source=BINS` — для ссылки
 *   «История печати (N)» и шторки журнала, плюс «Перепечатать».
 *
 * После каждой печати журнал перечитывается (`reload`), а не дописывается
 * руками — задачу создаёт сервер (один источник правды). Перепечатка берёт
 * ТЕКУЩИЙ справочник ячеек (стеллаж мог пополниться, архивная ячейка — 422).
 * «Скрыть файл» — общий `useLabelJobHide`, тоже с перечитыванием журнала.
 */

import { useCallback, useEffect, useState } from "react";

import { binLabelErrorText } from "./bin-label-print";
import { useNotify } from "../../ui/NotifyProvider";
import { resultTitle } from "../labels/label-texts";
import { useLabelJobHide } from "../labels/useLabelJobHide";
import { labelsApi, type LabelPrintJobDto } from "../../../lib/api-labels";

export function useBinLabelJobs(warehouseId: string, enabled: boolean) {
  const notify = useNotify();
  const [jobs, setJobs] = useState<LabelPrintJobDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const { hidingId, hideFile } = useLabelJobHide(reload);

  useEffect(() => {
    if (!warehouseId || !enabled) {
      setJobs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    labelsApi
      .jobs({ warehouseId, source: "BINS" })
      .then((list) => {
        if (!cancelled) setJobs(list);
      })
      .catch(() => {
        // Журнал — справка: без него печать работает, ссылку просто не покажем.
        if (!cancelled) setJobs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, enabled, version]);

  /** Перепечатать задачу: PDF — ссылкой в тосте (открытие только по клику). */
  const reprint = useCallback(
    async (job: LabelPrintJobDto) => {
      setReprintingId(job.id);
      try {
        const res = await labelsApi.reprint(job.id);
        const file = res.files[0];
        notify.success(resultTitle(res.sheetsCount, res.labelsCount), {
          action: file
            ? { label: "Открыть", onClick: () => window.open(file.url, "_blank", "noopener") }
            : undefined,
        });
        reload();
      } catch (err) {
        notify.error(binLabelErrorText(err).text);
      } finally {
        setReprintingId(null);
      }
    },
    [notify, reload],
  );

  return { jobs, jobsLoading: loading, reprintingId, reprint, reload, hidingId, hideFile };
}
